<template>
  <header class="toolbar">
    <div class="container cf p-0">
      <div class="menu">
        <slot name="menu"></slot>
        <div class="menu-group left">
          <toolbar-item
            v-if="hasSimplePermissions('viewProjectOverview')"
            :title="$t('Project Overview')"
            :to="{ name: 'ProjectOverview', params: { _id: currentProject._id ? currentProject._id : 0 } }"
            :disabled="!(currentProject && currentProject._id)"
          />

          <toolbar-item :title="$t('Project List')" :to="{ name: 'ProjectList' }" />

          <toolbar-item
            v-if="hasSimplePermissions('fetchPersons')"
            :title="$t('Organisation')"
            :to="{ name: 'OrganisationOverview' }"
          />

          <toolbar-item
            v-if="isDebugMode"
            :title="$t('View Project Object')"
            :to="{ name: 'ProjectObject', params: { _id: currentProject._id } }"
            :disabled="!(currentProject && currentProject._id)"
          />

          <toolbar-item
            v-if="isKubaVisible"
            href="https://www.h2entreprenad.se/system/valkommen-till-kuba"
            target="_blank"
            :title="$t('Kuba')"
          />
        </div>

        <div class="logo">
          <img src="static/BOON_header_logo.svg" />
        </div>

        <div class="menu-group right">
          <b-dropdown
            no-caret
            size="lg"
            variant="link"
            toggle-class="custom-header-user-menu-item text-decoration-none"
          >
            <template #button-content>
              <b-icon-triangle-fill class="mr-2 mb-1" font-scale="0.6" rotate="180"></b-icon-triangle-fill>
              <img class="flag-icon" :src="require(`@/assets/img/flags/${locale}.svg`)" :alt="locale" />
            </template>
            <b-dropdown-item-button v-for="lang in languages" :key="lang" @click="switchLanguage(lang)">
              <img class="flag-icon mr-2" :src="require(`@/assets/img/flags/${lang}.svg`)" :alt="lang" />
              {{ $t(lang) }}
            </b-dropdown-item-button>
          </b-dropdown>

          <b-dropdown
            v-if="hasSimplePermissions('getPartComments')"
            right
            no-caret
            size="lg"
            variant="link"
            toggle-class="custom-header-user-menu-item text-decoration-none"
            :disabled="!loggedInUserPartComments.length"
            @shown="messagesRead"
          >
            <template #button-content>
              <b-iconstack font-scale="2">
                <b-icon-person-fill stacked shift-h="-3" shift-v="-2"></b-icon-person-fill>
                <b-icon-chat-dots stacked scale="0.5" shift-h="5" shift-v="4"></b-icon-chat-dots>
              </b-iconstack>
              <b-badge v-if="unreadMessages" pill class="comments-counter round-badge">
                {{ unreadMessages }}
              </b-badge>
            </template>

            <b-dropdown-text :key="comment._id" class="notif-item" v-for="(comment, index) in loggedInUserPartComments">
              <HeaderComment :comment="comment" />
              <hr v-if="index < loggedInUserPartComments.length - 1" class="mt-2 mb-0" />
            </b-dropdown-text>
          </b-dropdown>

          <b-dropdown
            no-caret
            size="lg"
            variant="link"
            toggle-class="custom-header-user-menu-item text-decoration-none"
          >
            <template #button-content>
              <person-badge :person="loggedInPerson" />
            </template>
            <b-dropdown-item-button @click="logout">{{ $t('Log Out') }}</b-dropdown-item-button>
          </b-dropdown>
        </div>
      </div>
      <div v-if="hasSubmenuSlots" class="submenu">
        <div class="submenu-left"><slot name="submenu-left" /></div>
        <div class="submenu-center"><slot name="submenu-center"></slot></div>
        <div class="submenu-right"><slot name="submenu-right" /></div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ToolbarItem from '@/components/toolbar/ToolbarItem';
import HeaderComment from '@/components/toolbar/HeaderComment';
import PersonBadge from '@/components/toolbar/PersonBadge';

import Roles from '@/store/roles';

export default {
  name: 'CommonHeader',

  props: {
    title: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: null,
    },
  },

  components: { HeaderComment, ToolbarItem, PersonBadge },

  methods: {
    ...mapActions(['logout', 'markMessagesRead']),

    switchLanguage(lang) {
      this.$i18n.set(lang);
    },

    messagesRead() {
      this.markMessagesRead();
    },
  },

  computed: {
    ...mapGetters([
      'currentProject',
      'hasSimplePermissions',
      'hasRoles',
      'isDebugMode',
      'isAdmin',
      'loggedInPerson',
      'loggedInUserPartComments',
    ]),

    locale() {
      return this.$i18n.locale();
    },

    languages() {
      return this.$i18n.locales().filter((p) => {
        return this.$i18n.locale() !== p;
      });
    },
    unreadMessages() {
      return this.loggedInUserPartComments.filter((p) => {
        return p.commentdate > this.loggedInPerson.lastCommentReadDate;
      }).length;
    },

    hasSubmenuSlots() {
      return !!(this.$slots['submenu-left'] || this.$slots['submenu-center'] || this.$slots['submenu-right']);
    },

    isKubaVisible() {
      return !this.hasRoles([Roles.SubContractor, Roles.Client]);
    },
  },
};
</script>

<style>
.menu-group .custom-header-user-menu-item {
  color: white !important;
  font-size: 0.75rem;
  font-weight: 600;
}
</style>

<style scoped>
.notif-item {
  width: 400px;
}

.b-dropdown-text {
  padding: 0.25rem 0.5rem;
}
.flag-icon {
  width: 32px;
  height: 25px;
  border-radius: 2px;
}

header {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  height: 115px;
  z-index: 20;
}

header .container {
  /* Header's height is 80px; subheader's height is 3.375rem */
  height: calc(80px + 3.375rem);
  /* background-color: #1c2731; */
  max-width: 100vw;
  display: flex;
  flex-flow: column nowrap;
}

.round-badge {
  background-color: #2e8ab2;
  color: white;
}

.menu {
  flex: 1 auto;
  display: flex;
  flex-flow: row wrap;
  text-align: right;
  margin: 0;
  text-transform: uppercase;
  border-bottom: 0;
  background-color: #051623;
  height: 80px;
}

.menu-group {
  width: 20%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}

.menu-group.left {
  align-items: stretch;
}

.menu-group.left .toolbar-item {
  display: flex;
}

.menu-group.left >>> .toolbar-item a {
  flex-grow: 1;
  display: flex;
  align-items: center;
}

.menu-group.right {
  justify-content: flex-end;
}

@media (min-width: 668px) {
  header .container {
    flex-flow: row wrap;
  }

  .menu-group {
    width: 40%;
  }
}

.menu-group.left >>> .toolbar-item > a {
  padding: 0 1.25rem;
}

.menu-group.left >>> .toolbar-item a:hover:not(.disabled) {
  background-color: #3a4652c0;
}

/* a {
    text-decoration: none;
  } */

/* a[disabled=disabled] {
    color: grey;
  } */

.title {
  float: left;
  padding-left: 15px;
}

.logo {
  max-width: 20%;
  flex: 1 auto;
  margin: auto 0;
  text-align: center;
}

.logo img {
  max-height: 42px;
}

.comments-counter {
  top: 5px;
  left: -17px;
  border-radius: 50%;
}

.submenu {
  background-color: #051623;
  flex: 1 100%;
  display: flex;
  width: 100%;
  height: 3.375rem;
}

.submenu-center,
.submenu-left,
.submenu-right {
  display: flex;
  align-items: center;
}

.submenu-center {
  justify-content: center;
}

.submenu-right {
  justify-content: flex-end;
  flex: 1;
}

.submenu-left {
  flex: 1;
}

.submenu-left .plain-text {
  margin-left: 25px;
  font-size: 14px;
}

.submenu-left .bold-text {
  margin-left: 25px;
  font-weight: 300;
  font-size: 14px;
}

input {
  background: transparent;
  color: white;
}

.submenu input {
  border: 1px solid white;
  border-radius: 20px;
  padding: 5px 10px;
  font-size: 0.8em;
  align-self: center;
}

.submenu input::placeholder {
  color: white;
}

.submenu-center .atwho-wrap {
  display: inline-flex;
}

.comments {
  max-height: 400px;
  overflow-y: scroll;
}

.commentWrapper {
  border-bottom: 1px solid #34363e;
}

.commentWrapper:last-of-type {
  border-bottom: none;
}
.phone-container {
  display: none;
}

@media (max-width: 767px) {
  header {
    background: transparent;
    height: 80px;
    align-items: center;
    display: flex;
  }
  .container {
    display: none !important;
  }
  .phone-container {
    display: flex;
    justify-content: space-between;
    width: 80%;
    margin: 0 auto;
  }
  .phone-container .logo {
    max-width: none;
    flex: unset;
  }
}
@media (max-width: 320px) {
  .phone-container {
    width: 90%;
  }
  header {
    height: 55px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/toolbar/CommonHeader.vue